import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowUpRight, Languages, Menu, Phone, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import profLogo from '../assets/prof-logo.png';
import '../chrome.css';
import { getPathLanguage, saveLanguage } from '../languagePreference';

const navCopy = {
  en: { brand: 'Al Professor Car Inspection', home: 'Home', services: 'Services', mobileInspect: 'Mobile Inspection', book: 'Book', insights: 'Insights', contact: 'Contact', cta: 'Inspect a car' },
  ar: { brand: 'البروفيسور لفحص السيارات', home: 'الرئيسية', services: 'الخدمات', mobileInspect: 'الفحص المتنقل', book: 'احجز', insights: 'المقالات', contact: 'اتصل بنا', cta: 'افحص سيارة' },
};

const PublicHeader = () => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const lang = i18n.language === 'en' ? 'en' : 'ar';
  const isRTL = lang === 'ar';
  const c = navCopy[lang];
  const isLanding = /^\/(ar|en)\/?$/.test(location.pathname);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const urlLang = getPathLanguage(location.pathname);
    if (urlLang) {
      saveLanguage(urlLang);
      if (i18n.language !== urlLang) i18n.changeLanguage(urlLang);
    }
  }, [location.pathname, i18n]);

  useEffect(() => {
    const update = () => setScrolled(window.scrollY > 28);
    update();
    window.addEventListener('scroll', update, { passive: true });
    return () => window.removeEventListener('scroll', update);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
  }, [isRTL, lang]);

  const goToServices = () => {
    setMenuOpen(false);
    if (!isLanding) {
      navigate(`/${lang}/`);
      window.setTimeout(() => document.getElementById('services-section')?.scrollIntoView({ behavior: 'smooth' }), 120);
      return;
    }
    document.getElementById('services-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const switchLanguage = () => {
    const next = lang === 'ar' ? 'en' : 'ar';
    const segments = location.pathname.split('/');
    segments[1] = next;
    saveLanguage(next);
    i18n.changeLanguage(next);
    navigate(segments.join('/') || `/${next}/`);
  };

  return (
    <header className={`site-header ${isLanding ? 'is-overlay' : 'is-solid'} ${scrolled ? 'is-scrolled' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <nav className="site-nav site-container" aria-label="Primary navigation">
        <Link to={`/${lang}/`} className="brand-mark" aria-label={c.brand}>
          <span className="brand-lockup">
            <img src={profLogo} alt="" />
            <span className="brand-name">{c.brand}</span>
          </span>
        </Link>
        <div className="desktop-nav">
          <Link to={`/${lang}/`}>{c.home}</Link>
          <button type="button" onClick={goToServices}>{c.services}</button>
          <Link to={`/${lang}/mobile-car-inspection`}>{c.mobileInspect}</Link>
          <Link to={`/${lang}/book`}>{c.book}</Link>
          <Link to={`/${lang}/blog`}>{c.insights}</Link>
          <a href="tel:+971561868236">{c.contact}</a>
        </div>
        <div className="nav-actions">
          <button className="language-toggle" type="button" onClick={switchLanguage} aria-label="Switch language">
            <Languages size={16} />{lang === 'ar' ? 'EN' : 'عربي'}
          </button>
          <a className="nav-cta" href="https://wa.me/971561868236?text=Hello%2C%20I%20would%20like%20to%20book%20a%20vehicle%20inspection." target="_blank" rel="noopener noreferrer">
            {c.cta}<ArrowUpRight size={16} />
          </a>
          <button className="menu-toggle" type="button" onClick={() => setMenuOpen((open) => !open)} aria-expanded={menuOpen} aria-label="Toggle menu">
            {menuOpen ? <X size={23} /> : <Menu size={23} />}
          </button>
        </div>
      </nav>
      <div className={`mobile-menu ${menuOpen ? 'is-open' : ''}`} aria-hidden={!menuOpen}>
        <div className="site-container mobile-menu-inner">
          <Link to={`/${lang}/`}><span>01</span>{c.home}</Link>
          <button type="button" onClick={goToServices}><span>02</span>{c.services}</button>
          <Link to={`/${lang}/mobile-car-inspection`}><span>03</span>{c.mobileInspect}</Link>
          <Link to={`/${lang}/book`}><span>04</span>{c.book}</Link>
          <Link to={`/${lang}/blog`}><span>05</span>{c.insights}</Link>
          <a href="tel:+971561868236"><span>06</span>{c.contact}<Phone size={18} /></a>
        </div>
      </div>
    </header>
  );
};

export default PublicHeader;
